import React, { useMemo, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import LoadingEllipsis from "../../../components/Loading/LoadingEllipsis";
import { DefaultTable } from "../../../components/Tables/subs";
import { NoDataMsg } from "../../../components/AppMessages";
import PromptFilters from "./PromptFilters";


interface Props {
    queryFn: () => Record<string, any>;
    queryKey: string;
    title: string;
    summaryData?: { sov?: number; average_sentiment?: number;[key: string]: any };
}

const getComparisonClass = (value: number | null | undefined, marketAvg: number | null | undefined): string => {
    if (value == null || value === 0 || marketAvg == null) return "";
    if (value > marketAvg) return "textGreen";
    if (value < marketAvg) return "textRed";
    return "";
};

const OverviewOfPromptPerformanceTable = withRouter((props: RouteComponentProps & Props) => {
    const { history, queryFn, queryKey, title, summaryData } = props;
    const [toggleValue, setToggleValue] = useState(true);

    const searchWithoutDomain = React.useMemo(() => {
        const p = new URLSearchParams(history.location.search);
        p.delete("domain");
        return p.toString();
    }, [history.location.search]);

    const { data, isFetching, refetch } = useQuery({
        queryKey: [queryKey, searchWithoutDomain],
        queryFn: () => queryFn(),
        enabled: false,
    });

    React.useEffect(() => {
        const urlDateRange = new URLSearchParams(history.location.search).get("date_range");
        if (urlDateRange) refetch();
    }, [searchWithoutDomain]);


    const columns = useMemo(
        () => [
            {
                id: "rank",
                Header: "",
                accessor: `rank`,
                sortType: "basicCustom",
            },
            {
                id: `prompt`,
                Header: `Prompt`,
                accessor: toggleValue ? "prompt_en" : "prompt",
                sortType: "alphanumericCustom",
            },
            {
                id: `source`,
                Header: `All Sources`,
                accessor: `source`,
                sortType: "alphanumericCustom",
                Cell: (cell: { value: string }) => {
                    const { value } = cell;
                    if (!value) return "-";
                    return value.length > 50 ? `${value.slice(0, 50)}...` : value;
                },
            },
            {
                id: `average_sentiment`,
                Header: `Average Sentiment`,
                accessor: `average_sentiment`,
                sortType: "alphanumericFalsyLast",
                Cell: (cell: { value: number }) => {
                    const { value } = cell;
                    return (
                        <span data-value={value} className={getComparisonClass(value, summaryData?.average_sentiment)}>
                            {value != null && value !== 0 ? `${value?.toFixed(1)}%` : "-"}
                        </span>
                    );
                }
            },

        ],
        [summaryData, toggleValue]
    );


    return (
        <div className="gdr_tile primary_tile_background">
            <div className="">
                <div className="h-30 grid-col-span-6 verbatimTile">
                    <div className="title_container">
                        <h3 className='summary_title'>{title}</h3>
                    </div>
                    <PromptFilters toggleValue={toggleValue} setToggleValue={setToggleValue} />
                    {isFetching ? (
                        <LoadingEllipsis isLoading={isFetching} />
                    ) : data?.length > 0 ?
                        (
                            <DefaultTable
                                columns={columns}
                                data={data ?? []}
                                id={"topicVerbatimTable"}
                                isChildComponent={false}
                                initialSortColId={{ id: `average_sentiment`, desc: true }}
                                initialSortBy={[{ id: `average_sentiment`, desc: true }, { id: `rank`, desc: false }]}
                                hiddenCols={[]}
                                extraClass="verbatimTable"
                                showPagination={true}
                                pageSize={10}
                            />
                        )
                        : (<div className="no_content"><NoDataMsg /></div>)
                    }
                </div>
            </div>
        </div>
    );
});

export default OverviewOfPromptPerformanceTable;